<template>
  <div>
    <v-btn-toggle
      v-model="periodType"
      mandatory
      dense
      color="red darken-1"
      class="d-flex justify-center mb-4"
    >
      <v-btn value="mes" class="px-8">
        MES
      </v-btn>
      <v-btn value="semestre" class="px-8">
        SEMESTRE
      </v-btn>
    </v-btn-toggle>

    <div class="d-flex justify-center">
      <v-date-picker
        v-if="periodType === 'mes'"
        v-model="selectedMonth"
        type="month"
        :max="maxMonth"
        color="red darken-1"
        width="280"
        locale="es"
        class="picker-border"
        @input="onMonthSelected"
      />

      <v-card v-if="periodType === 'semestre'" class="period-card elevation-4" style="border-radius: 4px; overflow: hidden; width: 280px;">
          
          <div class="red darken-1 pa-4 white--text">
            <div class="text-subtitle-1">Seleccione un semestre:</div>
          </div>

          <!-- Navegación de año -->
          <div class="d-flex align-center justify-center py-3" :style="semestreBodyStyle">
            <v-btn icon small @click="prevYear">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-menu
              v-model="yearMenu"
              :close-on-content-click="false"
              offset-y
              max-height="250"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  class="text-subtitle-1 font-weight-bold"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ semestreYear }}
                </v-btn>
              </template>
              <v-card>
                <v-list dense class="year-list">
                  <v-list-item
                    v-for="year in yearList"
                    :key="year"
                    :class="{ 'v-list-item--active red--text font-weight-bold': year === semestreYear }"
                    @click="selectYear(year)"
                  >
                    <v-list-item-title class="text-center">{{ year }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
            <v-btn icon small :disabled="semestreYear >= currentYear" @click="nextYear">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          
          <div class="px-4 pb-4 pt-2 text-center" :style="semestreBodyStyle">
            <v-btn
              block
              :text="selectedSemestre !== 1"
              :color="selectedSemestre === 1 ? '#e57373' : ''"
              :dark="selectedSemestre === 1"
              :disabled="isSemestreDisabled(1)"
              class="mb-3 py-5"
              @click="selectSemestre(1)"
            >
              Primer Semestre
            </v-btn>
            <v-btn
              block
              :text="selectedSemestre !== 2"
              :color="selectedSemestre === 2 ? '#e57373' : ''"
              :dark="selectedSemestre === 2"
              :disabled="isSemestreDisabled(2)"
              class="py-5"
              @click="selectSemestre(2)"
            >
              Segundo Semestre
            </v-btn>
          </div>
        </v-card>
    </div>

    <v-alert
      type="info"
      text
      dense
      class="mt-4 mb-0"
      :style="{ visibility: (fechaInicio && fechaFin) ? 'visible' : 'hidden' }"
    >
      <strong>Período a reportar:</strong> {{ fechaInicioFormatted || '01/01/2026' }} — {{ fechaFinFormatted || '31/01/2026' }}
    </v-alert>
  </div>
</template>

<script>
export default {
  name: 'AtsPeriodSelector',

  data() {
    const now = new Date()
    return {
      periodType: 'mes',
      selectedMonth: null,
      semestreYear: now.getFullYear(),
      selectedSemestre: null,
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth(),
      fechaInicio: '',
      fechaFin: '',
      yearMenu: false,
    }
  },

  computed: {
    yearList() {
      const years = []
      for (let y = this.currentYear; y >= this.currentYear - 20; y--) {
        years.push(y)
      }
      return years
    },
    maxMonth() {
      const y = this.currentYear
      const m = String(this.currentMonth + 1).padStart(2, '0')
      return `${y}-${m}`
    },
    fechaInicioFormatted() {
      return this.formatDate(this.fechaInicio)
    },
    fechaFinFormatted() {
      return this.formatDate(this.fechaFin)
    },
    isDark() {
      return this.$vuetify.theme.dark
    },
    semestreBodyStyle() {
      return {
        backgroundColor: this.isDark ? '#424242' : '#FFFFFF',
      }
    },
  },

  watch: {
    periodType() {
      this.selectedMonth = null
      this.selectedSemestre = null
      this.clearDates()
    },
    semestreYear() {
      this.selectedSemestre = null
      this.clearDates()
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      const [y, m, d] = dateStr.split('-')
      return `${d}/${m}/${y}`
    },
    onMonthSelected(value) {
      const [year, month] = value.split('-').map(Number)
      const periodoInformado = String(month).padStart(2, '0')
      const fechaInicio = `${year}-${periodoInformado}-01`
      const lastDay = new Date(year, month, 0).getDate()
      const fechaFin = `${year}-${periodoInformado}-${String(lastDay).padStart(2, '0')}`
      this.fechaInicio = fechaInicio
      this.fechaFin = fechaFin
      this.$emit('update:fechaInicio', fechaInicio)
      this.$emit('update:fechaFin', fechaFin)
      this.$emit('update:periodoInformado', periodoInformado)
      this.$emit('update:tipoPeriodicidad', 'mensual')
    },
    selectYear(year) {
      this.semestreYear = year
      this.yearMenu = false
    },
    prevYear() {
      this.semestreYear--
    },
    nextYear() {
      if (this.semestreYear < this.currentYear) {
        this.semestreYear++
      }
    },
    isSemestreDisabled(semestre) {
      if (this.semestreYear > this.currentYear) return true
      if (this.semestreYear < this.currentYear) return false
      if (semestre === 2 && this.currentMonth < 6) return true
      return false
    },
    selectSemestre(semestre) {
      this.selectedSemestre = semestre
      const year = this.semestreYear
      let fechaInicio, fechaFin
      if (semestre === 1) {
        fechaInicio = `${year}-01-01`
        fechaFin = `${year}-06-30`
      } else {
        fechaInicio = `${year}-07-01`
        fechaFin = `${year}-12-31`
      }
      this.fechaInicio = fechaInicio
      this.fechaFin = fechaFin
      this.$emit('update:fechaInicio', fechaInicio)
      this.$emit('update:fechaFin', fechaFin)
      this.$emit('update:periodoInformado', semestre === 1 ? '06' : '12')
      this.$emit('update:tipoPeriodicidad', 'semestral')
    },
    clearDates() {
      this.fechaInicio = ''
      this.fechaFin = ''
      this.$emit('update:fechaInicio', '')
      this.$emit('update:fechaFin', '')
      this.$emit('update:periodoInformado', '')
      this.$emit('update:tipoPeriodicidad', '')
    },
    isValid() {
      if (this.periodType === 'mes') return !!this.selectedMonth
      if (this.periodType === 'semestre') return !!this.selectedSemestre
      return false
    },
  },
}
</script>

<style scoped>
.period-card {
  max-width: 280px;
  margin: 0 auto;
}
.year-list {
  min-width: 120px;
}
.picker-border {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.theme--dark .picker-border {
  border-color: rgba(255, 255, 255, 0.12);
}
</style>
